/**
 * @module ReasoningClusterCard
 * @purpose Single cluster card with expand/collapse, view-mode toggle, and Canvas drill-down.
 * @depends useClusterDetail, useReasoningArtefactBatch, ReasoningCommentGroup, ReasoningPathLadder
 * @dependedOnBy CanvasReasoningPanel (via virtualised list)
 *
 * @rules
 * - Performance: lazy-fetches ClusterDetail only when expanded; ResizeObserver reports height
 * - State: local expand + viewMode toggles; resolved observations fetched on expand
 * - Errors: detail loading/error states rendered inline within the card body
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ChevronDown, ChevronRight } from 'lucide-react'
import type {
  ClusterSummary,
  ClusterDetail,
  ClusterExplanationLine,
  ClusterBriefing,
  HashResolution,
  ResolvedObservation,
} from '../../../../types/reasoning'
import { useClusterDetail, useReasoningArtefactBatch } from '../../../../hooks/use-reasoning'
import { useClusterBriefing } from '../../../../hooks/use-cluster-briefing'
import { resolveHashConflict } from '../../../../services/reasoning-ai'
import { ReasoningCommentGroup } from './ReasoningCommentGroup'
import { ReasoningPathLadder } from './ReasoningPathLadder'
import { ClusterBriefingSummary } from './briefing/ClusterBriefingSummary'
import { FactPackPanel } from './briefing/FactPackPanel'
import { NotEstablishedPanel } from './briefing/NotEstablishedPanel'

type ViewMode = 'summary' | 'facts' | 'reasoningPath'

const TOP_BASENAMES_PREVIEW = 3

interface ReasoningClusterCardProps {
  caseId: string
  runId: string
  cluster: ClusterSummary
  expanded: boolean
  onToggleExpanded: () => void
  onHeightChange?: (height: number) => void
  onDrillToCluster: (resolved: Map<string, ResolvedObservation>) => void
}

const CHIP_BASE: React.CSSProperties = {
  fontFamily: 'var(--font-mono)',
  fontSize: '0.58rem',
  padding: '1px 6px',
  borderRadius: '4px',
  display: 'inline-block',
  marginRight: '4px',
}
const RANK_CHIP: React.CSSProperties = {
  ...CHIP_BASE,
  background: 'var(--accent)',
  color: 'var(--btn-primary-text, #fff)',
}
const NEUTRAL_CHIP: React.CSSProperties = {
  ...CHIP_BASE,
  background: 'transparent',
  border: '1px solid var(--border)',
  color: 'var(--text-muted)',
}
const TOGGLE_BTN: React.CSSProperties = {
  fontFamily: 'var(--font-mono)',
  fontSize: '0.62rem',
  padding: '2px 8px',
  borderRadius: '4px',
  background: 'transparent',
  border: '1px solid var(--border)',
  color: 'var(--text-muted)',
  cursor: 'pointer',
  marginRight: '4px',
}
const TOGGLE_BTN_ACTIVE: React.CSSProperties = {
  ...TOGGLE_BTN,
  background: 'var(--accent)',
  color: 'var(--btn-primary-text, #fff)',
  border: 'none',
}
const OPEN_IN_CANVAS_BTN: React.CSSProperties = {
  marginTop: '8px',
  fontFamily: 'var(--font-mono)',
  fontSize: '0.68rem',
  padding: '4px 10px',
  borderRadius: '6px',
  background: 'var(--accent)',
  color: 'var(--btn-primary-text, #fff)',
  border: 'none',
  cursor: 'pointer',
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
}

function EvidenceChips({ cluster }: { cluster: ClusterSummary }): React.ReactElement {
  const { t } = useTranslation('analysis')
  return (
    <>
      {cluster.hasPresenceEvidence && (
        <span style={NEUTRAL_CHIP}>{t('reasoning.clusterCard.evidence.presence')}</span>
      )}
      {cluster.hasReferenceEvidence && (
        <span style={NEUTRAL_CHIP}>{t('reasoning.clusterCard.evidence.reference')}</span>
      )}
      {cluster.hasExecutionEvidence && (
        <span style={NEUTRAL_CHIP}>{t('reasoning.clusterCard.evidence.execution')}</span>
      )}
    </>
  )
}

interface CardHeaderProps {
  cluster: ClusterSummary
  expanded: boolean
  onToggleExpanded: () => void
}

function CardHeader({ cluster, expanded, onToggleExpanded }: CardHeaderProps): React.ReactElement {
  const { t } = useTranslation('analysis')
  return (
    <button
      type="button"
      onClick={onToggleExpanded}
      aria-expanded={expanded}
      style={{
        width: '100%',
        textAlign: 'left',
        padding: '8px 12px',
        background: 'transparent',
        border: 'none',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'flex-start',
        gap: '6px',
      }}
    >
      {expanded
        ? <ChevronDown className="mt-0.5 h-3 w-3 shrink-0" style={{ color: 'var(--text-muted)' }} />
        : <ChevronRight className="mt-0.5 h-3 w-3 shrink-0" style={{ color: 'var(--text-muted)' }} />}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontFamily: 'var(--font-display)', fontSize: '0.8rem', color: 'var(--text)', marginBottom: '4px' }}>
          {t('reasoning.clusterCard.label')}
          {' — '}
          {t('reasoning.clusterCard.directoryPrefix')}
          {' '}
          {cluster.canonicalDirectory}
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '2px', alignItems: 'center' }}>
          <span style={RANK_CHIP}>{t('reasoning.clusterCard.rank', { position: cluster.rankPosition })}</span>
          <span style={NEUTRAL_CHIP}>{t('reasoning.clusterCard.fileCount', { count: cluster.fileCount })}</span>
          <EvidenceChips cluster={cluster} />
        </div>
        {!expanded && cluster.topBasenames.length > 0 && (
          <div style={{ fontSize: '0.68rem', color: 'var(--text-muted)', marginTop: '2px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {cluster.topBasenames.slice(0, TOP_BASENAMES_PREVIEW).join(', ')}
          </div>
        )}
      </div>
    </button>
  )
}

interface CardViewContentProps {
  detail: ClusterDetail
  viewMode: ViewMode
  resolvedMap: Map<string, ResolvedObservation>
  briefing: ClusterBriefing | null
  aiUnavailable: boolean
  briefingLoading: boolean
  onViewAllInCanvas: () => void
  onResolveHash: (resolution: HashResolution) => Promise<void>
}

/** Renders the active drill-down view: L1 summary, L2 fact pack, or L3 verify ladder. */
function CardViewContent({
  detail,
  viewMode,
  resolvedMap,
  briefing,
  aiUnavailable,
  briefingLoading,
  onViewAllInCanvas,
  onResolveHash,
}: CardViewContentProps): React.ReactElement {
  if (viewMode === 'facts') {
    return <FactPackPanel detail={detail} briefing={briefing} onResolveHash={onResolveHash} />
  }
  if (viewMode === 'reasoningPath') {
    return (
      <>
        <ReasoningPathLadder cluster={detail} resolved={resolvedMap} />
        <NotEstablishedPanel notEstablished={briefing?.not_established ?? []} />
      </>
    )
  }
  return (
    <>
      <ClusterBriefingSummary briefing={briefing} aiUnavailable={aiUnavailable} loading={briefingLoading} />
      {detail.explanations.map((line: ClusterExplanationLine) => (
        <ReasoningCommentGroup
          key={line.commentType}
          line={line}
          resolved={resolvedMap}
          onViewAllInCanvas={onViewAllInCanvas}
        />
      ))}
    </>
  )
}

interface CardBodyProps {
  caseId: string
  runId: string
  clusterId: string
  resolvedMap: Map<string, ResolvedObservation>
  viewMode: ViewMode
  briefing: ClusterBriefing | null
  aiUnavailable: boolean
  briefingLoading: boolean
  onViewAllInCanvas: () => void
  onDrillToCluster: (resolved: Map<string, ResolvedObservation>) => void
}

function CardBody(props: CardBodyProps): React.ReactElement {
  const { t } = useTranslation('analysis')
  const { detail, loading, error, refetch } = useClusterDetail(props.caseId, props.runId, props.clusterId)

  const handleResolveHash = useCallback(async (resolution: HashResolution): Promise<void> => {
    await resolveHashConflict({ caseId: props.caseId, runId: props.runId, clusterId: props.clusterId, resolution })
    await refetch()
  }, [props.caseId, props.runId, props.clusterId, refetch])

  if (loading) {
    return (
      <div role="status" aria-busy="true" style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
        {t('reasoning.loading.title')}
      </div>
    )
  }
  if (error !== null) {
    return (
      <div role="alert" style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
        {error}
      </div>
    )
  }
  if (detail === null) return <></>

  return (
    <>
      <CardViewContent
        detail={detail}
        viewMode={props.viewMode}
        resolvedMap={props.resolvedMap}
        briefing={props.briefing}
        aiUnavailable={props.aiUnavailable}
        briefingLoading={props.briefingLoading}
        onViewAllInCanvas={props.onViewAllInCanvas}
        onResolveHash={handleResolveHash}
      />
      <button type="button" onClick={() => { props.onDrillToCluster(props.resolvedMap) }} style={OPEN_IN_CANVAS_BTN}>
        {t('reasoning.clusterCard.openInCanvas')}
      </button>
    </>
  )
}

interface ViewModeToggleProps {
  viewMode: ViewMode
  onChange: (mode: ViewMode) => void
}

/** Three-way drill-down toggle: Summary (L1) · Fact pack (L2) · Reasoning path (L3). */
function ViewModeToggle({ viewMode, onChange }: ViewModeToggleProps): React.ReactElement {
  const { t } = useTranslation('analysis')
  const btn = (mode: ViewMode): React.CSSProperties =>
    viewMode === mode ? TOGGLE_BTN_ACTIVE : TOGGLE_BTN
  return (
    <div style={{ display: 'flex', gap: '4px', marginBottom: '10px' }}>
      <button type="button" style={btn('summary')} onClick={() => { onChange('summary') }}>
        {t('reasoning.clusterCard.viewMode.summary')}
      </button>
      <button type="button" style={btn('facts')} onClick={() => { onChange('facts') }}>
        {t('reasoning.clusterCard.viewMode.facts')}
      </button>
      <button type="button" style={btn('reasoningPath')} onClick={() => { onChange('reasoningPath') }}>
        {t('reasoning.clusterCard.viewMode.reasoningPath')}
      </button>
    </div>
  )
}

/**
 * Collapsible cluster card with lazy detail fetch and view-mode toggle.
 *
 * @behaviour Renders collapsed chip-summary when not expanded.
 *            On expand: fetches ClusterDetail and resolves contributing observations.
 *            View-mode toggle switches between Summary (comment groups) and Reasoning Path ladder.
 *            ResizeObserver callback reports measured height for react-window.
 * @errors Inline loading/error text within the card body; no full-panel error.
 */
export function ReasoningClusterCard({
  caseId,
  runId,
  cluster,
  expanded,
  onToggleExpanded,
  onHeightChange,
  onDrillToCluster,
}: ReasoningClusterCardProps): React.ReactElement {
  const [viewMode, setViewMode] = useState<ViewMode>('summary')
  const [resolvedMap, setResolvedMap] = useState<Map<string, ResolvedObservation>>(new Map())
  const containerRef = useRef<HTMLDivElement>(null)
  const { resolve } = useReasoningArtefactBatch(caseId, runId)
  const { detail } = useClusterDetail(caseId, runId, expanded ? cluster.clusterId : null)
  const { briefing, aiUnavailable, loading: briefingLoading } = useClusterBriefing(
    caseId,
    runId,
    expanded ? cluster.clusterId : null,
  )

  useEffect(() => {
    if (!detail) return
    const allIds = detail.explanations.flatMap((line: ClusterExplanationLine) => line.contributingObservationIds)
    if (allIds.length === 0) return
    void resolve(allIds).then((map) => { setResolvedMap(map) })
  }, [detail, resolve])

  useEffect(() => {
    if (!onHeightChange || !containerRef.current) return
    const observer = new ResizeObserver(() => {
      if (containerRef.current) onHeightChange(containerRef.current.offsetHeight)
    })
    observer.observe(containerRef.current)
    return () => { observer.disconnect() }
  }, [onHeightChange])

  const handleViewAllInCanvas = useCallback(() => {
    onDrillToCluster(resolvedMap)
  }, [resolvedMap, onDrillToCluster])

  return (
    <div
      ref={containerRef}
      style={{ borderBottom: '1px solid var(--border)', background: 'var(--bg-card)' }}
    >
      <CardHeader cluster={cluster} expanded={expanded} onToggleExpanded={onToggleExpanded} />
      {expanded && (
        <div style={{ padding: '0 12px 12px 12px' }}>
          <ViewModeToggle viewMode={viewMode} onChange={setViewMode} />
          <CardBody
            caseId={caseId}
            runId={runId}
            clusterId={cluster.clusterId}
            resolvedMap={resolvedMap}
            viewMode={viewMode}
            briefing={briefing}
            aiUnavailable={aiUnavailable}
            briefingLoading={briefingLoading}
            onViewAllInCanvas={handleViewAllInCanvas}
            onDrillToCluster={onDrillToCluster}
          />
        </div>
      )}
    </div>
  )
}
